"use client";

import { TimeFrame } from "charts/src/types/chart";
import get from "lodash/get";
import { useEffect, useRef, useState } from "react";

import { ErrorView } from "@/components/error-view";
import {
  ChartConfig,
  useChartConfigList,
} from "@/features/analysis/services/get-chart-config-list";
import { ChartConfigListPaths } from "@/features/analysis/services/get-chart-config-list";
import { usePrevious } from "@/hooks/use-previous";
import { Lang } from "@/i18n-config";
import {
  RevenueBreakdownConfigType,
  getRevenueBreakdownConfigSchema,
  usePublicData,
} from "@/services/public-data";
import { getTranslations } from "@/utils/i18n/get-translations";

import { LoadingIndicator } from "ui/core/button/LoadingIndicator";

import { CustomizeCharts } from "../data/chart-configs";
import { isChartSupportGivenTimeFrame } from "../utils";
import { ChartGridItem } from "./ChartGridItem";

type ChartLayoutProps = {
  title?: string;
  symbol: string;
  lang: Lang;
  chartCategoryPath: ChartConfigListPaths;
  timeframe: TimeFrame;
  emptyHandlingPolicy?: "hide-block" | "show-empty-view";
};

const TimeframeLimitMap = {
  Q: 20,
  Y: 15,
  "4Q": 20,
  D: 20,
};

export const ChartGridLayout = ({
  title,
  symbol,
  lang,
  chartCategoryPath,
  timeframe,
  emptyHandlingPolicy = "hide-block",
}: ChartLayoutProps) => {
  const { data: chartConfigs, isFetching } = useChartConfigList(symbol);
  const [isBlockEmpty, setIsBlockEmpty] = useState(false);
  const previousTimeframe = usePrevious(timeframe);
  const ref = useRef<HTMLDivElement>(null);

  const { t } = getTranslations(lang);
  const { data: { allowList } = { allowList: [] } } =
    usePublicData<RevenueBreakdownConfigType>({
      itemName: "revenue-breakdown-config",
      schema: getRevenueBreakdownConfigSchema,
    });

  useEffect(() => {
    if (timeframe !== previousTimeframe) {
      setIsBlockEmpty(false);
    }
  }, [timeframe, previousTimeframe]);

  const rawConfig = get(chartConfigs, chartCategoryPath, []);
  const config: ChartConfig[] = Array.isArray(rawConfig) ? rawConfig : [];

  const isByPassAllowList =
    typeof window !== "undefined" &&
    window.location.hash === "#by-pass-allow-list";

  const filteredConfig = config
    .filter(({ chartId }) =>
      chartId === CustomizeCharts.revenueBreakdownChart.id
        ? isByPassAllowList || allowList.includes(symbol)
        : true
    )
    .filter(({ timeframe: timeframeOption }) => {
      return isChartSupportGivenTimeFrame(timeframeOption, timeframe);
    });

  const isContainCharts = filteredConfig.length > 0;

  if (
    (emptyHandlingPolicy === "hide-block" && isBlockEmpty) ||
    !isContainCharts
  ) {
    return null;
  }

  return (
    <>
      {title && <h3 className="mb-4 mt-8">{title}</h3>}

      {isFetching && (
        <div className="mt-52 flex h-full w-full items-center justify-center">
          <LoadingIndicator />
        </div>
      )}

      {isBlockEmpty && (
        <ErrorView errorType="empty" className="my-20">
          <p>{t.error.noData}</p>
        </ErrorView>
      )}

      <div
        className="grid h-full w-full grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
        ref={ref}
      >
        {filteredConfig.map(({ chartId, timeframe: timeframeOption }) => (
          <ChartGridItem
            key={chartId + timeframe}
            symbol={symbol}
            lang={lang}
            chartId={chartId}
            timeFrameOption={timeframeOption}
            timeFrame={timeframe}
            limit={TimeframeLimitMap[timeframe]}
            renderCallback={() => {
              setIsBlockEmpty((ref.current?.childNodes.length ?? 0) === 0);
            }}
          />
        ))}
      </div>
    </>
  );
};
